import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getFirestore, collection, getDocs } from '@firebase/firestore'
import { app } from '../../firebase.js'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowsRotate, faChartColumn, faChartPie, faGenderless, faMagnifyingGlass, faUsers } from '@fortawesome/free-solid-svg-icons'
import LeftBar from '../../Components/LeftBar'
import Dashnav from '../../Components/Dashnav'
import '../../Pages/dash.css'

const countFormatter = new Intl.NumberFormat('en-KE')

const formatCount = (value) => countFormatter.format(Number(value || 0))

const normalizeText = (value) => String(value || '').trim()
const normalizeStatus = (value) => normalizeText(value).toLowerCase()

const convertedStatuses = ['Student', 'Completed', 'Deferred', 'Expelled', 'Dropped']
const notConvertedStatuses = ['inactive', 'Did Not Join']
const convertedStatusSet = new Set(convertedStatuses.map(normalizeStatus))
const notConvertedStatusSet = new Set(notConvertedStatuses.map(normalizeStatus))

const GENDER_COLORS = {
  Male: '#0f766e',
  Female: '#e11d48',
  'Prefer not to say': '#8b5cf6',
  Unknown: '#94a3b8'
}

const formatPercent = (value) => {
  const safeValue = Number.isFinite(value) ? value : 0
  return `${safeValue.toFixed(1)}%`
}

const getAgeGroup = (age) => {
  if (age === null || age === undefined || age === '') return null

  const numericAge = Number(age)
  if (Number.isNaN(numericAge)) return null

  if (numericAge < 20) return '<20'
  if (numericAge <= 24) return '20-24'
  if (numericAge <= 30) return '25-30'
  if (numericAge <= 39) return '30-39'
  return '40+'
}

const DemographicsTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const row = payload[0]?.payload || {}

  return (
    <div className="demographicsTooltip">
      <strong>{label}</strong>
      <div className="demographicsTooltipRow">
        <span>Count</span>
        <strong>{formatCount(row.count || row.value)}</strong>
      </div>
      {typeof row.conversionRate === 'number' && (
        <div className="demographicsTooltipRow">
          <span>Conversion rate</span>
          <strong>{formatPercent(row.conversionRate)}</strong>
        </div>
      )}
      {row.converted !== undefined && (
        <div className="demographicsTooltipRow">
          <span>Converted</span>
          <strong>{formatCount(row.converted)}</strong>
        </div>
      )}
      {row.notConverted !== undefined && (
        <div className="demographicsTooltipRow">
          <span>Not converted</span>
          <strong>{formatCount(row.notConverted)}</strong>
        </div>
      )}
    </div>
  )
}

function DemographicsBySource() {
  const db = getFirestore(app)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [rows, setRows] = useState([])
  const [searchTerm, setSearchTerm] = useState('')

  const fetchData = useCallback(async (options = {}) => {
    const initialLoad = options.initial === true

    if (initialLoad) {
      setLoading(true)
    } else {
      setRefreshing(true)
    }

    setError('')

    try {
      const q = collection(db, 'users')
      const querySnapshot = await getDocs(q)

      const genderCounts = {
        Male: 0,
        Female: 0,
        'Prefer not to say': 0,
        Unknown: 0
      }

      const ageCounts = {
        '<20': 0,
        '20-24': 0,
        '25-30': 0,
        '30-39': 0,
        '40+': 0,
        Unknown: 0
      }

      const conversionGroups = {
        '<20': { converted: 0, notConverted: 0 },
        '20-24': { converted: 0, notConverted: 0 },
        '25-30': { converted: 0, notConverted: 0 },
        '30-39': { converted: 0, notConverted: 0 },
        '40+': { converted: 0, notConverted: 0 }
      }

      const genderConversions = {
        Male: { converted: 0, notConverted: 0 },
        Female: { converted: 0, notConverted: 0 },
        'Prefer not to say': { converted: 0, notConverted: 0 },
        Unknown: { converted: 0, notConverted: 0 }
      }

      querySnapshot.forEach((docItem) => {
        const data = docItem.data()

        const gender = normalizeText(data.gender)
        const normalizedGender = gender || 'Unknown'
        const selectedGender = ['Male', 'Female', 'Prefer not to say'].includes(normalizedGender)
          ? normalizedGender
          : 'Unknown'
        genderCounts[selectedGender] += 1

        const ageGroup = getAgeGroup(data.age)
        if (ageGroup) {
          ageCounts[ageGroup] += 1
        } else {
          ageCounts.Unknown += 1
        }

        const status = normalizeStatus(data.userStatus) || 'inactive'
        const isConverted = convertedStatusSet.has(status)
        const isNotConverted = notConvertedStatusSet.has(status)

        if (ageGroup && conversionGroups[ageGroup]) {
          if (isConverted) {
            conversionGroups[ageGroup].converted += 1
          } else if (isNotConverted) {
            conversionGroups[ageGroup].notConverted += 1
          }
        }

        if (selectedGender) {
          if (isConverted) {
            genderConversions[selectedGender].converted += 1
          } else if (isNotConverted) {
            genderConversions[selectedGender].notConverted += 1
          }
        }
      })

      const ageOrder = ['<20', '20-24', '25-30', '30-39', '40+']

      const normalizedRows = ageOrder.map((group) => {
        const converted = conversionGroups[group].converted
        const notConverted = conversionGroups[group].notConverted
        const total = converted + notConverted

        return {
          ageGroup: group,
          count: ageCounts[group] || 0,
          converted,
          notConverted,
          total,
          conversionRate: total > 0 ? (converted / total) * 100 : 0
        }
      })

      const genderRows = Object.keys(genderCounts).map((gender) => {
        const converted = genderConversions[gender].converted
        const notConverted = genderConversions[gender].notConverted
        const total = converted + notConverted

        return {
          gender,
          value: genderCounts[gender],
          converted,
          notConverted,
          total,
          conversionRate: total > 0 ? (converted / total) * 100 : 0
        }
      })

      setRows([
        {
          type: 'gender',
          items: genderRows
        },
        {
          type: 'age',
          items: normalizedRows
        }
      ])
    } catch (fetchError) {
      console.error('Failed to load demographics by source:', fetchError)
      setRows([])
      setError('We could not load the demographics analysis right now. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [db])

  useEffect(() => {
    fetchData({ initial: true })
  }, [fetchData])

  const genderRows = useMemo(() => rows.find((entry) => entry.type === 'gender')?.items || [], [rows])
  const ageRows = useMemo(() => rows.find((entry) => entry.type === 'age')?.items || [], [rows])

  const filteredGenderRows = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()
    if (!search) return genderRows
    return genderRows.filter((row) => row.gender.toLowerCase().includes(search))
  }, [genderRows, searchTerm])

  const filteredAgeRows = useMemo(() => {
    const search = searchTerm.trim().toLowerCase()
    if (!search) return ageRows
    return ageRows.filter((row) => row.ageGroup.toLowerCase().includes(search))
  }, [ageRows, searchTerm])

  const genderTotal = useMemo(() => {
    return genderRows.reduce((sum, row) => sum + row.value, 0)
  }, [genderRows])

  const ageTotal = useMemo(() => {
    return ageRows.reduce((sum, row) => sum + row.count, 0)
  }, [ageRows])

  const totalRecords = Math.max(genderTotal, ageTotal)
  const totalConverted = useMemo(() => {
    return ageRows.reduce((sum, row) => sum + row.converted, 0)
  }, [ageRows])
  const totalNotConverted = useMemo(() => {
    return ageRows.reduce((sum, row) => sum + row.notConverted, 0)
  }, [ageRows])

  const genderPieData = useMemo(() => {
    return filteredGenderRows.map((row) => ({
      name: row.gender,
      value: row.value
    }))
  }, [filteredGenderRows])

  const ageChartData = useMemo(() => filteredAgeRows, [filteredAgeRows])

  const genderConversionChart = useMemo(() => {
    return filteredGenderRows.map((row) => ({
      ...row
    }))
  }, [filteredGenderRows])

  const topGender = genderRows.reduce((current, row) => {
    if (!current || row.value > current.value) {
      return row
    }
    return current
  }, null)

  const topAgeGroup = ageRows.reduce((current, row) => {
    if (!current || row.count > current.count) {
      return row
    }
    return current
  }, null)

  const conversionRate = totalRecords > 0 ? (totalConverted / totalRecords) * 100 : 0
  const ageCoverage = ageRows.reduce((sum, row) => sum + row.count, 0)
  const ageKnownRate = totalRecords > 0 ? (ageCoverage / totalRecords) * 100 : 0

  const topGenderRate = topGender && topGender.value > 0 ? (topGender.converted / Math.max(topGender.total, 1)) * 100 : 0
  const topAgeRate = topAgeGroup && topAgeGroup.total > 0 ? (topAgeGroup.converted / Math.max(topAgeGroup.total, 1)) * 100 : 0

  const hasAnyRows = genderRows.length > 0 || ageRows.length > 0

  return (
    <div className="mainContainer demographicsBySourcePage">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />

        <div className="demographicsBySourceContent">
          <div className="appsHeader">
            <div className="attendanceHeaderCopy">
              <p className="appsKicker">Data Analytics</p>
              <h4>Demographics by Source</h4>
              <p className="appsSubtext">
                Review gender and age patterns together, then compare how conversion behaves across those groups.
              </p>
            </div>

            <div className="appsHeaderStats">
              <div className="appsStatChip">
                <span>Records</span>
                <strong>{formatCount(totalRecords)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Conversion rate</span>
                <strong>{formatPercent(conversionRate)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Age known</span>
                <strong>{formatPercent(ageKnownRate)}</strong>
              </div>
              <div className="appsStatChip">
                <span>Top gender</span>
                <strong>{topGender ? topGender.gender : '—'}</strong>
              </div>
            </div>
          </div>

          <div className="appsFiltersCard demographicsFiltersCard">
            <div className="appsSearch input-group demographicsSearch">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </span>
              <input
                className="form-control"
                type="text"
                placeholder="Search gender or age group..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="demographicsFiltersMeta">
              <span>{formatCount(filteredGenderRows.length)} gender slices visible</span>
              <span>{formatCount(filteredAgeRows.length)} age groups visible</span>
            </div>
            <Button variant="outline-dark" onClick={() => fetchData()} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {error && (
            <div className="incomeStateCard mb-3">
              <p>{error}</p>
              <Button variant="outline-dark" onClick={() => fetchData()} disabled={refreshing}>
                <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                Try again
              </Button>
            </div>
          )}

          <div className="demographicsSummaryGrid">
            <div className="demographicsSummaryCard">
              <span>Leading gender</span>
              <strong>{topGender ? topGender.gender : '—'}</strong>
              <p>{topGender ? `${formatCount(topGender.value)} records` : 'No data yet'}</p>
            </div>
            <div className="demographicsSummaryCard">
              <span>Leading age band</span>
              <strong>{topAgeGroup ? topAgeGroup.ageGroup : '—'}</strong>
              <p>{topAgeGroup ? `${formatCount(topAgeGroup.count)} records` : 'No data yet'}</p>
            </div>
            <div className="demographicsSummaryCard">
              <span>Converted</span>
              <strong>{formatCount(totalConverted)}</strong>
              <p>Across age segments with recorded values.</p>
            </div>
            <div className="demographicsSummaryCard">
              <span>Not converted</span>
              <strong>{formatCount(totalNotConverted)}</strong>
              <p>Mostly driven by inactive and did-not-join statuses.</p>
            </div>
          </div>

          <div className="demographicsGrid">
            <div className="appsTableCard demographicsChartCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faChartPie} className="me-2" />
                  Gender distribution
                </h6>
                <span>Current student records</span>
              </div>
              <div className="demographicsChartWrap">
                {loading ? (
                  <div className="incomeStateCard">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading demographics...</span>
                    </Spinner>
                    <p>Loading demographics...</p>
                  </div>
                ) : genderPieData.length ? (
                  <div className="demographicsChartBox demographicsChartBox--pie">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={genderPieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={84}
                          outerRadius={140}
                          paddingAngle={4}
                          label
                        >
                          {genderPieData.map((entry, index) => (
                            <Cell key={`gender-cell-${entry.name}-${index}`} fill={GENDER_COLORS[entry.name] || '#94a3b8'} />
                          ))}
                        </Pie>
                        <Tooltip content={<DemographicsTooltip />} />
                        <Legend verticalAlign="bottom" height={32} iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#64748b' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="incomeStateCard">
                    <p>No gender data available.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="appsInsightsCard demographicsInsightsCard">
              <div className="appsInsightsHeader">
                <h6>
                  <FontAwesomeIcon icon={faGenderless} className="me-2" />
                  Demographic signals
                </h6>
                <span>{formatCount(hasAnyRows ? 4 : 0)} tracked groups</span>
              </div>

              <div className="appsInsightsList">
                <div className="appsInsightsItem">
                  <div>
                    <strong>Conversion</strong>
                    <span>Across all records with known age groups</span>
                  </div>
                  <small>{formatPercent(conversionRate)}</small>
                </div>
                <div className="appsInsightsItem">
                  <div>
                    <strong>Age coverage</strong>
                    <span>Records with age captured</span>
                  </div>
                  <small>{formatPercent(ageKnownRate)}</small>
                </div>
                <div className="appsInsightsItem">
                  <div>
                    <strong>Top gender</strong>
                    <span>{topGender ? `${topGender.gender} records` : 'No data yet'}</span>
                  </div>
                  <small>{topGender ? formatPercent(topGenderRate) : '0.0%'}</small>
                </div>
                <div className="appsInsightsItem">
                  <div>
                    <strong>Top age band</strong>
                    <span>{topAgeGroup ? `${topAgeGroup.ageGroup} records` : 'No data yet'}</span>
                  </div>
                  <small>{topAgeGroup ? formatPercent(topAgeRate) : '0.0%'}</small>
                </div>
              </div>

              <p className="appsInsightsMore">
                Search a demographic label to focus the charts and table, or refresh after records change.
              </p>
            </div>
          </div>

          <div className="demographicsGrid demographicsGrid--secondary">
            <div className="appsTableCard demographicsChartCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faChartColumn} className="me-2" />
                  Age distribution
                </h6>
                <span>Known ages only</span>
              </div>
              <div className="demographicsChartWrap">
                {loading ? (
                  <div className="incomeStateCard">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading age distribution...</span>
                    </Spinner>
                    <p>Loading age distribution...</p>
                  </div>
                ) : ageChartData.length ? (
                  <div className="demographicsChartBox">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ageChartData} margin={{ top: 8, right: 18, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                        <XAxis dataKey="ageGroup" tick={{ fontSize: 12, fill: '#475569' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                        <Tooltip content={<DemographicsTooltip />} />
                        <Legend verticalAlign="top" height={28} iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#64748b' }} />
                        <Bar dataKey="count" name="Users" fill="#0f766e" radius={[10, 10, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="incomeStateCard">
                    <p>No age data available.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="appsTableCard demographicsChartCard">
              <div className="appsTableHeader">
                <h6>
                  <FontAwesomeIcon icon={faChartColumn} className="me-2" />
                  Conversion by gender
                </h6>
                <span>Converted vs not converted</span>
              </div>
              <div className="demographicsChartWrap">
                {loading ? (
                  <div className="incomeStateCard">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading conversion by gender...</span>
                    </Spinner>
                    <p>Loading conversion by gender...</p>
                  </div>
                ) : genderConversionChart.length ? (
                  <div className="demographicsChartBox">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={genderConversionChart} margin={{ top: 8, right: 18, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                        <XAxis dataKey="gender" tick={{ fontSize: 12, fill: '#475569' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                        <Tooltip content={<DemographicsTooltip />} />
                        <Legend verticalAlign="top" height={28} iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#64748b' }} />
                        <Bar dataKey="converted" name="Converted" fill="#0f766e" radius={[10, 10, 0, 0]} />
                        <Bar dataKey="notConverted" name="Not converted" fill="#ef4444" radius={[10, 10, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="incomeStateCard">
                    <p>No gender conversion data available.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="appsTableCard demographicsTableCard">
            <div className="appsTableHeader">
              <h6>
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Demographic summary
              </h6>
              <span>{formatCount(totalRecords)} records reviewed</span>
            </div>
            <div className="appsTableWrap">
              {loading ? (
                <div className="incomeStateCard">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading demographic summary...</span>
                  </Spinner>
                  <p>Loading demographic summary...</p>
                </div>
              ) : (
                <Table hover responsive className="appsTable demographicsTable">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Count</th>
                      <th>Converted</th>
                      <th>Not Converted</th>
                      <th>Total</th>
                      <th>Conversion Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {genderRows.map((row) => (
                      <tr key={`gender-${row.gender}`}>
                        <td>
                          <strong>{row.gender}</strong>
                        </td>
                        <td>{formatCount(row.value)}</td>
                        <td>{formatCount(row.converted)}</td>
                        <td>{formatCount(row.notConverted)}</td>
                        <td>{formatCount(row.total)}</td>
                        <td>{formatPercent(row.conversionRate)}</td>
                      </tr>
                    ))}
                    <tr className="demographicsTableDivider">
                      <td colSpan="6"></td>
                    </tr>
                    {ageRows.map((row) => (
                      <tr key={`age-${row.ageGroup}`}>
                        <td>
                          <strong>{row.ageGroup}</strong>
                        </td>
                        <td>{formatCount(row.count)}</td>
                        <td>{formatCount(row.converted)}</td>
                        <td>{formatCount(row.notConverted)}</td>
                        <td>{formatCount(row.total)}</td>
                        <td>{formatPercent(row.conversionRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DemographicsBySource
